import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from './collections';

// Payment Management Service
export const paymentManagementService = {
  // Helper function to clean undefined values from objects
  cleanObject(obj) {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(item => this.cleanObject(item));
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.cleanObject(value);
      }
    }
    return cleaned;
  },

  // Save payment transaction
  async savePaymentTransaction(transactionData) {
    try {
      // Clean the transaction data to remove undefined values
      const cleanedData = this.cleanObject(transactionData);
      
      const docRef = await addDoc(collection(db, COLLECTIONS.PAYMENT_TRANSACTIONS), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, transactionId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update payment transaction status
  async updatePaymentStatus(transactionId, status, additionalData = {}) {
    try {
      const transactionRef = doc(db, COLLECTIONS.PAYMENT_TRANSACTIONS, transactionId);
      await updateDoc(transactionRef, {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get payment transaction by ID
  async getPaymentTransaction(transactionId) {
    try {
      const transactionRef = doc(db, COLLECTIONS.PAYMENT_TRANSACTIONS, transactionId);
      const transactionSnap = await getDoc(transactionRef);
      
      if (transactionSnap.exists()) {
        return { 
          success: true, 
          transaction: { id: transactionSnap.id, ...transactionSnap.data() } 
        };
      } else {
        return { success: false, error: 'Transaction not found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get user payment history
  async getUserPaymentHistory(userId, limitCount = 50) {
    try {
      const q = query(
        collection(db, COLLECTIONS.PAYMENT_TRANSACTIONS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const transactions = [];
      
      querySnapshot.forEach((doc) => {
        transactions.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, transactions };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save teacher hiring payment
  async saveTeacherHiringPayment(hiringPaymentData) {
    try {
      const cleanedData = this.cleanObject(hiringPaymentData);
      
      const docRef = await addDoc(collection(db, COLLECTIONS.TEACHER_HIRING_PAYMENTS), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, paymentId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save course enrollment payment
  async saveCourseEnrollmentPayment(enrollmentPaymentData) {
    try {
      const cleanedData = this.cleanObject(enrollmentPaymentData);
      
      const docRef = await addDoc(collection(db, COLLECTIONS.COURSE_ENROLLMENT_PAYMENTS), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, paymentId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save coaching subscription payment
  async saveCoachingSubscriptionPayment(subscriptionPaymentData) {
    try {
      const cleanedData = this.cleanObject(subscriptionPaymentData);
      
      const docRef = await addDoc(collection(db, COLLECTIONS.COACHING_SUBSCRIPTION_PAYMENTS), {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, paymentId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Process successful payment
  async processSuccessfulPayment(transactionData) {
    try {
      // Save the main transaction
      const transactionResult = await this.savePaymentTransaction({
        ...transactionData,
        status: 'completed',
        paymentDate: serverTimestamp()
      });

      if (!transactionResult.success) {
        return transactionResult;
      }

      // Handle specific payment types
      if (transactionData.paymentType === 'teacher_hiring') {
        await this.saveTeacherHiringPayment({
          transactionId: transactionResult.transactionId,
          ...transactionData.hiringData,
          status: 'completed'
        });
      } else if (transactionData.paymentType === 'course_enrollment') {
        await this.saveCourseEnrollmentPayment({
          transactionId: transactionResult.transactionId,
          ...transactionData.enrollmentData,
          status: 'completed'
        });
      } else if (transactionData.paymentType === 'coaching_subscription') {
        await this.saveCoachingSubscriptionPayment({
          transactionId: transactionResult.transactionId,
          ...transactionData.metadata,
          status: 'completed'
        });
      } else if (transactionData.paymentType === 'admission_application') {
        // Admission application payments are handled by admissionsService
        // Just log the transaction here
      }

      return { success: true, transactionId: transactionResult.transactionId };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Process failed payment
  async processFailedPayment(transactionData) {
    try {
      const transactionResult = await this.savePaymentTransaction({
        ...transactionData,
        status: 'failed',
        failureDate: serverTimestamp()
      });

      return transactionResult;
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get payment statistics
  async getPaymentStatistics(userId = null) {
    try {
      let q = query(collection(db, COLLECTIONS.PAYMENT_TRANSACTIONS));
      
      if (userId) {
        q = query(
          collection(db, COLLECTIONS.PAYMENT_TRANSACTIONS),
          where('userId', '==', userId)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const stats = {
        totalTransactions: 0,
        completedTransactions: 0,
        failedTransactions: 0,
        totalAmount: 0,
        completedAmount: 0
      };
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        stats.totalTransactions++;
        
        if (data.status === 'completed') {
          stats.completedTransactions++;
          stats.completedAmount += data.amount || 0;
        } else if (data.status === 'failed') {
          stats.failedTransactions++;
        }
        
        stats.totalAmount += data.amount || 0;
      });
      
      return { success: true, stats };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get teacher earnings from completed hiring payments
  async getTeacherEarnings(teacherUserId) {
    try {
      // First, find the teacher's document ID from their userId
      const teacherQuery = query(
        collection(db, 'tuitionTeachers'),
        where('userId', '==', teacherUserId)
      );
      const teacherSnapshot = await getDocs(teacherQuery);
      
      if (teacherSnapshot.empty) {
        return { success: true, totalEarnings: 0 };
      }
      
      const teacherDoc = teacherSnapshot.docs[0];
      const teacherId = teacherDoc.id; // This is the document ID used in payment records
      
      // Query teacher hiring payments where teacherId matches and status is completed
      const paymentsQuery = query(
        collection(db, COLLECTIONS.TEACHER_HIRING_PAYMENTS),
        where('teacherId', '==', teacherId),
        where('paymentStatus', '==', 'completed')
      );
      
      const paymentsSnapshot = await getDocs(paymentsQuery);
      let totalEarnings = 0;
      
      paymentsSnapshot.forEach((doc) => {
        const data = doc.data();
        // Sum up payment amounts - check multiple possible field names
        const amount = data.paymentAmount || data.totalAmount || data.amount || data.sessionCost || 0;
        totalEarnings += typeof amount === 'number' ? amount : 0;
      });
      
      return { success: true, totalEarnings };
    } catch (error) {
      // If query fails (e.g., index not created), return 0 earnings
      return { success: false, error: error.message, totalEarnings: 0 };
    }
  }
};
